import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tooltip, Button, Select, Switch } from "antd";
import { ReloadOutlined, ArrowRightOutlined } from "@ant-design/icons";
import {
  TbCurrencyDollar,
  TbShoppingCart,
  TbReceipt,
  TbDiscount,
  TbAlertTriangle,
  TbCreditCard,
  TbBuildingStore,
  TbTrendingUp,
  TbClock,
  TbAlertCircle,
  TbHome,
  TbSchool,
  TbToolsKitchen2,
  TbShirt,
  TbHeartbeat,
  TbPalette,
  TbBan,
} from "react-icons/tb";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import dayjs from "dayjs";
import {
  useSaleSummary,
  GetSummaryParamsI,
  getAllBranches,
  useGastosResumen,
} from "@apis";
import { BranchStkI, SaleSummaryI } from "@interfaces";
import { useRouterStore } from "@stores";
import { SubscriptionStatusEnum } from "@enums";
import "../../css/admin.css";

type DashPeriod = "today" | "week";

const COLORS = [
  "#1e1948",
  "#1D9E75",
  "#534AB7",
  "#854F0B",
  "#0C447C",
  "#A32D2D",
];

const PERIOD_OPTIONS: { value: DashPeriod; label: string }[] = [
  { value: "today", label: "Hoy" },
  { value: "week", label: "Esta semana" },
];

// Íconos por código de categoría de gasto (Tabler, mismo set que ya usa el dashboard)
const ICONOS_CATEGORIA: Record<string, React.ReactNode> = {
  VIVIENDA: <TbHome size={16} />,
  EDUCACION: <TbSchool size={16} />,
  ALIMENTACION: <TbToolsKitchen2 size={16} />,
  VESTIMENTA: <TbShirt size={16} />,
  SALUD: <TbHeartbeat size={16} />,
  ARTE_CULTURA: <TbPalette size={16} />,
  SIN_CLASIFICAR: <TbBan size={16} />,
};

const CARGAS_OPTIONS = [0, 1, 2, 3, 4, 5].map((n) => ({
  value: n,
  label:
    n === 5
      ? "5 o más cargas"
      : n === 0
        ? "Sin cargas"
        : `${n} carga${n > 1 ? "s" : ""}`,
}));

// ── KPI card ─────────────────────────────────────────────
const KpiCard = ({
  icon,
  label,
  value,
  sub,
  color,
  trend,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  color: string;
  trend?: { value: number; label: string };
}) => (
  <div className="da-kpi-card">
    <div className="da-kpi-top">
      <div className="da-kpi-icon" style={{ background: `${color}18`, color }}>
        {icon}
      </div>
      {trend && (
        <span className={`da-kpi-trend ${trend.value >= 0 ? "up" : "down"}`}>
          {trend.value >= 0 ? "↑" : "↓"} {Math.abs(trend.value)}%
        </span>
      )}
    </div>
    <span className="da-kpi-value">{value}</span>
    <span className="da-kpi-label">{label}</span>
    {sub && <span className="da-kpi-sub">{sub}</span>}
  </div>
);

// ── componente principal ──────────────────────────────────
const DashboardAdminComponent = () => {
  const navigate = useNavigate();
  const subscriptionStatusCode = useRouterStore(
    (s) => s.subscriptionStatusCode,
  );
  const daysLeft = useRouterStore((s) => s.daysLeft);
  const firstName = useRouterStore((s) => s.firstName);
  const [branches, setBranches] = React.useState<BranchStkI[]>([]);
  const [branchId, setBranchId] = React.useState<string | undefined>();
  const [period, setPeriod] = useState<DashPeriod>("today");

  // ── Gastos deducibles (Impuesto a la Renta) ──
  const anioActual = new Date().getFullYear();
  const [anioGastos, setAnioGastos] = useState<number>(anioActual);
  const [cargasFamiliares, setCargasFamiliares] = useState<number>(0);
  const [esCatastrofica, setEsCatastrofica] = useState<boolean>(false);

  React.useEffect(() => {
    getAllBranches()
      .then((res) => setBranches(res.data ?? []))
      .catch(() => {});
  }, []);

  const summaryParams = useMemo<GetSummaryParamsI>(
    () => ({ period, branchId }),
    [period, branchId],
  );

  const { summaryQuery } = useSaleSummary(summaryParams);
  const {
    data: summaryData,
    isLoading: loadingSummary,
    refetch,
    isFetching,
  } = summaryQuery;

  const summary: SaleSummaryI | undefined = summaryData?.data;

  const { resumenQuery } = useGastosResumen({
    anio: anioGastos,
    cargasFamiliares,
    esCatastrofica,
  });
  const {
    data: gastosData,
    isLoading: loadingGastos,
    isFetching: fetchingGastos,
  } = resumenQuery;
  const gastos = gastosData?.data;
  const isLoadingGastos = loadingGastos || fetchingGastos;

  const fmt = (n?: number) =>
    `$${(n ?? 0).toLocaleString("es-EC", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const fmtNum = (n?: number) => (n ?? 0).toLocaleString("es-EC");

  const isLoading = loadingSummary || isFetching;

  // ── skeleton ──────────────────────────────────────────
  const Skeleton = ({ w = "100%", h = 20 }: { w?: string; h?: number }) => (
    <div className="da-skeleton" style={{ width: w, height: h }} />
  );

  return (
    <div className="da-page">
      {subscriptionStatusCode === SubscriptionStatusEnum.ABOUT_TO_EXPIRE && (
        <div
          className={`da-sub-banner ${daysLeft !== null && daysLeft <= 2 ? "danger" : "warning"}`}
        >
          <div className="da-sub-banner-icon">
            {daysLeft !== null && daysLeft <= 2 ? (
              <TbAlertCircle size={18} />
            ) : (
              <TbClock size={18} />
            )}
          </div>
          <div className="da-sub-banner-text">
            {daysLeft === 0 ? (
              <>
                <strong>Tu suscripción vence hoy.</strong> Contacta a tu
                administrador para renovar y evitar perder el acceso.
              </>
            ) : daysLeft === 1 ? (
              <>
                <strong>Tu suscripción vence mañana.</strong> Contacta a tu
                administrador para renovar.
              </>
            ) : (
              <>
                <strong>Tu suscripción vence en {daysLeft} días.</strong>{" "}
                Comunícate con tu administrador para renovar a tiempo.
              </>
            )}
          </div>
          <div className="da-sub-banner-days">
            {daysLeft !== null && daysLeft >= 0 && (
              <span
                className={`da-sub-days-badge ${daysLeft <= 2 ? "danger" : "warning"}`}
              >
                {daysLeft === 0 ? "Vence hoy" : `${daysLeft}d restantes`}
              </span>
            )}
          </div>
        </div>
      )}

      {/* ── barra superior ── */}
      <div className="da-topbar">
        <div className="da-topbar-left">
          <h2 className="da-title">{`Bienvenido, ${firstName}`}</h2>
          <p className="da-subtitle">
            {dayjs().format("dddd, DD [de] MMMM YYYY")}
          </p>
        </div>

        <div className="da-topbar-right">
          {/* período */}
          <div className="da-period-tabs">
            {PERIOD_OPTIONS.map((p) => (
              <button
                key={p.value}
                type="button"
                className={`da-period-btn ${period === p.value ? "active" : ""}`}
                onClick={() => setPeriod(p.value)}
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* sucursal */}
          <Select
            allowClear
            size="small"
            placeholder="Todas las sucursales"
            className="da-select"
            value={branchId}
            onChange={setBranchId}
            options={branches.map((b) => ({ value: b.id, label: b.name }))}
          />

          <Tooltip title="Refrescar">
            <Button
              shape="circle"
              icon={<ReloadOutlined />}
              size="small"
              loading={isLoading}
              onClick={() => refetch()}
              className="da-refresh"
            />
          </Tooltip>

          <Button
            size="small"
            type="dashed"
            icon={<ArrowRightOutlined />}
            className="da-btn-advanced"
            onClick={() => navigate("/stockify/history/show")}
          >
            Más avanzado
          </Button>
        </div>
      </div>

      {/* ── KPIs ── */}
      <div className="da-kpi-grid">
        {isLoading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="da-kpi-card da-kpi-skeleton">
              <Skeleton w="40px" h={40} />
              <Skeleton w="70%" h={24} />
              <Skeleton w="50%" h={14} />
            </div>
          ))
        ) : (
          <>
            <KpiCard
              icon={<TbCurrencyDollar size={20} />}
              label="Total vendido"
              value={fmt(summary?.totals.totalSales)}
              sub={`${fmtNum(summary?.totals.countSales)} ventas`}
              color="#1D9E75"
            />
            <KpiCard
              icon={<TbShoppingCart size={20} />}
              label="Ticket promedio"
              value={fmt(summary?.totals.averageTicket)}
              color="#534AB7"
            />
            <KpiCard
              icon={<TbReceipt size={20} />}
              label="IVA total"
              value={fmt(summary?.totals.totalIva)}
              color="#0C447C"
            />
            <KpiCard
              icon={<TbDiscount size={20} />}
              label="Descuentos"
              value={fmt(summary?.totals.totalDiscount)}
              color="#854F0B"
            />
            <KpiCard
              icon={<TbCreditCard size={20} />}
              label="CxC pendiente"
              value={fmt(summary?.receivables.totalPending)}
              sub={`${fmtNum(summary?.receivables.count)} cuentas`}
              color="#A32D2D"
            />
            <KpiCard
              icon={<TbAlertTriangle size={20} />}
              label="Stock bajo"
              value={String(summary?.lowStockCount ?? 0)}
              sub="productos bajo mínimo"
              color="#E8593C"
            />
          </>
        )}
      </div>

      {/* ── fila gráficas principales ── */}
      <div className="da-charts-row">
        {/* ventas por día */}
        <div className="da-chart-card da-chart-main">
          <div className="da-chart-header">
            <span className="da-chart-title">
              <TbTrendingUp size={14} />
              {period === "today" ? "Ventas por hora" : "Ventas por día"}
            </span>
            <span className="da-chart-total">
              {isLoading ? "—" : fmt(summary?.totals.totalSales)}
            </span>
          </div>
          {isLoading ? (
            <div className="da-chart-loading">
              <Skeleton w="100%" h={200} />
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart
                data={summary?.byDay ?? []}
                margin={{ top: 4, right: 8, left: 0, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="gradSales" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#1D9E75" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#1D9E75" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0eefb" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10, fill: "#888780" }}
                  tickFormatter={(v) =>
                    period === "today"
                      ? dayjs(v).format("HH:mm")
                      : dayjs(v).format("DD/MM")
                  }
                />
                <YAxis
                  tick={{ fontSize: 10, fill: "#888780" }}
                  tickFormatter={(v) => `$${v}`}
                  width={55}
                />
                <RTooltip
                  formatter={(v: number) => [fmt(v), "Total"]}
                  labelFormatter={(l) => dayjs(l).format("DD MMM YYYY")}
                  contentStyle={{
                    fontSize: 12,
                    borderRadius: 8,
                    border: "1px solid #e0dff5",
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="total"
                  stroke="#1D9E75"
                  strokeWidth={2}
                  fill="url(#gradSales)"
                  dot={{ r: 3, fill: "#1D9E75" }}
                  activeDot={{ r: 5 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* donut método de pago */}
        <div className="da-chart-card">
          <div className="da-chart-header">
            <span className="da-chart-title">Método de pago</span>
          </div>
          {isLoading ? (
            <div className="da-chart-loading">
              <Skeleton w="160px" h={160} />
            </div>
          ) : (summary?.byPaymentMethod ?? []).length === 0 ? (
            <div className="da-chart-empty">Sin datos</div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={summary?.byPaymentMethod ?? []}
                  dataKey="total"
                  nameKey="method"
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={78}
                  paddingAngle={3}
                >
                  {(summary?.byPaymentMethod ?? []).map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <RTooltip
                  formatter={(v: number) => [fmt(v), "Total"]}
                  contentStyle={{
                    fontSize: 12,
                    borderRadius: 8,
                    border: "1px solid #e0dff5",
                  }}
                />
                <Legend
                  iconType="circle"
                  iconSize={7}
                  wrapperStyle={{ fontSize: 11 }}
                />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* ── fila inferior ── */}
      <div className="da-bottom-row">
        {/* sucursales */}
        <div className="da-chart-card">
          <div className="da-chart-header">
            <span className="da-chart-title">
              <TbBuildingStore size={14} /> Por sucursal
            </span>
          </div>
          {isLoading ? (
            <Skeleton w="100%" h={160} />
          ) : (summary?.byBranch ?? []).length === 0 ? (
            <div className="da-chart-empty">Sin datos</div>
          ) : (
            <ResponsiveContainer width="100%" height={160}>
              <BarChart
                data={summary?.byBranch ?? []}
                layout="vertical"
                margin={{ top: 0, right: 12, left: 0, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#f0eefb"
                  horizontal={false}
                />
                <XAxis
                  type="number"
                  tick={{ fontSize: 10, fill: "#888780" }}
                  tickFormatter={(v) => `$${v}`}
                />
                <YAxis
                  type="category"
                  dataKey="branch"
                  tick={{ fontSize: 10, fill: "#888780" }}
                  width={80}
                />
                <RTooltip
                  formatter={(v: number) => [fmt(v), "Total"]}
                  contentStyle={{
                    fontSize: 12,
                    borderRadius: 8,
                    border: "1px solid #e0dff5",
                  }}
                />
                <Bar
                  dataKey="total"
                  fill="#534AB7"
                  radius={[0, 4, 4, 0]}
                  maxBarSize={18}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* top productos */}
        <div className="da-chart-card">
          <div className="da-chart-header">
            <span className="da-chart-title">Top 5 productos</span>
          </div>
          {isLoading ? (
            <div className="da-top-list">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} w="100%" h={36} />
              ))}
            </div>
          ) : (summary?.topProducts ?? []).length === 0 ? (
            <div className="da-chart-empty">Sin ventas en el período</div>
          ) : (
            <div className="da-top-list">
              {(summary?.topProducts ?? []).map((p, i) => {
                const maxQty = Math.max(
                  ...(summary?.topProducts ?? [{ quantity: 1 }]).map(
                    (x) => x.quantity,
                  ),
                );
                const pct = Math.round((p.quantity / maxQty) * 100);
                return (
                  <div key={i} className="da-top-item">
                    <span className="da-top-rank">{i + 1}</span>
                    <div className="da-top-info">
                      <span className="da-top-name">{p.product}</span>
                      <div className="da-top-bar-bg">
                        <div
                          className="da-top-bar-fill"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                    <div className="da-top-nums">
                      <span className="da-top-qty">
                        {fmtNum(p.quantity)} und
                      </span>
                      <span className="da-top-total">{fmt(p.total)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* alertas */}
        <div className="da-chart-card da-alerts-card">
          <div className="da-chart-header">
            <span className="da-chart-title">
              <TbAlertTriangle size={14} /> Alertas
            </span>
          </div>
          <div className="da-alerts-list">
            {/* stock bajo */}
            <div
              className={`da-alert-item ${(summary?.lowStockCount ?? 0) > 0 ? "warn" : "ok"}`}
            >
              <div className="da-alert-icon">
                <TbAlertTriangle size={16} />
              </div>
              <div className="da-alert-info">
                <span className="da-alert-label">Stock bajo</span>
                <span className="da-alert-value">
                  {isLoading ? "—" : `${summary?.lowStockCount ?? 0} productos`}
                </span>
              </div>
              {(summary?.lowStockCount ?? 0) > 0 && (
                <span className="da-alert-badge warn">
                  {summary?.lowStockCount}
                </span>
              )}
            </div>

            {/* CxC pendiente */}
            <div
              className={`da-alert-item ${(summary?.receivables.count ?? 0) > 0 ? "danger" : "ok"}`}
            >
              <div className="da-alert-icon">
                <TbCreditCard size={16} />
              </div>
              <div className="da-alert-info">
                <span className="da-alert-label">CxC pendientes</span>
                <span className="da-alert-value">
                  {isLoading ? "—" : fmt(summary?.receivables.totalPending)}
                </span>
              </div>
              {(summary?.receivables.count ?? 0) > 0 && (
                <span className="da-alert-badge danger">
                  {summary?.receivables.count}
                </span>
              )}
            </div>

            {/* descuentos aplicados */}
            <div className="da-alert-item neutral">
              <div className="da-alert-icon">
                <TbDiscount size={16} />
              </div>
              <div className="da-alert-info">
                <span className="da-alert-label">Descuentos aplicados</span>
                <span className="da-alert-value">
                  {isLoading ? "—" : fmt(summary?.totals.totalDiscount)}
                </span>
              </div>
            </div>

            {/* ventas del período */}
            <div className="da-alert-item ok">
              <div className="da-alert-icon">
                <TbShoppingCart size={16} />
              </div>
              <div className="da-alert-info">
                <span className="da-alert-label">Ventas registradas</span>
                <span className="da-alert-value">
                  {isLoading
                    ? "—"
                    : `${fmtNum(summary?.totals.countSales)} ventas`}
                </span>
              </div>
            </div>
          </div>

          <button
            type="button"
            className="da-btn-goto"
            onClick={() => navigate("/stockify/history/show")}
          >
            Ver reportes completos <ArrowRightOutlined />
          </button>
        </div>
      </div>

      {/* ── Gastos deducibles (Impuesto a la Renta) ── */}
      <div className="da-gastos-row">
        <div className="da-chart-card da-gastos-card">
          <div className="da-chart-header da-gastos-header">
            <span className="da-chart-title">
              <TbReceipt size={14} /> Gastos Deducibles Impuesto a la Renta
            </span>
            <div className="da-gastos-filtros">
              <Select
                size="small"
                className="da-select"
                value={anioGastos}
                onChange={setAnioGastos}
                options={[anioActual, anioActual - 1, anioActual - 2].map(
                  (y) => ({ value: y, label: String(y) }),
                )}
              />
              <Select
                size="small"
                className="da-select"
                value={cargasFamiliares}
                onChange={setCargasFamiliares}
                options={CARGAS_OPTIONS}
              />
              <Tooltip title="Enfermedad catastrófica, rara/huérfana o discapacidad — cambia el tope a 100 canastas">
                <div className="da-gastos-catastrofica">
                  <span>Catastrófica</span>
                  <Switch
                    size="small"
                    checked={esCatastrofica}
                    onChange={setEsCatastrofica}
                  />
                </div>
              </Tooltip>
            </div>
          </div>

          {isLoadingGastos ? (
            <div className="da-chart-loading">
              <Skeleton w="100%" h={220} />
            </div>
          ) : !gastos || gastos.categorias.every((c) => c.monto === 0) ? (
            <div className="da-chart-empty">
              Sin gastos clasificados en {anioGastos}
            </div>
          ) : (
            <div className="da-gastos-body">
              {/* donut */}
              <div className="da-gastos-donut">
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={gastos.categorias}
                      dataKey="monto"
                      nameKey="nombre"
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={85}
                      paddingAngle={2}
                    >
                      {gastos.categorias.map((c, i) => (
                        <Cell
                          key={i}
                          fill={c.color ?? COLORS[i % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <RTooltip
                      formatter={(v: number) => [fmt(v), "Gasto"]}
                      contentStyle={{
                        fontSize: 12,
                        borderRadius: 8,
                        border: "1px solid #e0dff5",
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>

                <div className="da-gastos-resumen-tope">
                  <div className="da-gastos-resumen-item">
                    <span className="da-gastos-resumen-label">
                      Total gastado
                    </span>
                    <span className="da-gastos-resumen-value">
                      {fmt(gastos.totalGeneral)}
                    </span>
                  </div>
                  <div className="da-gastos-resumen-item">
                    <span className="da-gastos-resumen-label">
                      Tope deducible
                    </span>
                    <span className="da-gastos-resumen-value">
                      {gastos.tope !== null
                        ? fmt(gastos.tope)
                        : "No configurado"}
                    </span>
                  </div>
                  <div className="da-gastos-resumen-item highlight">
                    <span className="da-gastos-resumen-label">
                      Rebaja estimada (18%)
                    </span>
                    <span className="da-gastos-resumen-value">
                      {gastos.rebajaEstimada !== null
                        ? fmt(gastos.rebajaEstimada)
                        : "—"}
                    </span>
                  </div>
                </div>
              </div>

              {/* detalle por categoría */}
              <div className="da-gastos-detalle">
                {gastos.categorias.map((c) => (
                  <div key={c.categoriaId} className="da-gastos-item">
                    <div
                      className="da-gastos-item-icon"
                      style={{
                        background: `${c.color ?? "#888780"}18`,
                        color: c.color ?? "#888780",
                      }}
                    >
                      {ICONOS_CATEGORIA[c.codigo] ?? <TbReceipt size={16} />}
                    </div>
                    <div className="da-gastos-item-info">
                      <div className="da-gastos-item-top">
                        <span className="da-gastos-item-name">{c.nombre}</span>
                        <span className="da-gastos-item-monto">
                          {fmt(c.monto)}
                        </span>
                      </div>
                      <div className="da-gastos-item-bar-bg">
                        <div
                          className="da-gastos-item-bar-fill"
                          style={{
                            width: `${c.porcentaje}%`,
                            background: c.color ?? "#888780",
                          }}
                        />
                      </div>
                      <span className="da-gastos-item-pct">
                        {c.porcentaje.toFixed(2)}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardAdminComponent;
